/**
 * GeolocationContext
 * Provides real-time geolocation tracking for resources (drivers/technicians)
 * Uses WebSocket for live updates with polling fallback
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "../hooks/useAuth";
import {
  useGetGeolocationsQuery,
  geolocationApi,
} from "../redux/services/geolocations/geolocationApi";
import { useDispatch } from "react-redux";
import { createSocketClient, getSocketUrl } from "../utils/socketAuth";

const GeolocationContext = createContext();

// Polling interval when WebSocket is disconnected (60 seconds)
const POLLING_INTERVAL = 60000;

// Freshness threshold for location data (30 minutes)
const FRESHNESS_MINUTES = 30;

/**
 * GeolocationProvider Component
 * Manages real-time geolocation state for the dispatch map
 */
export const GeolocationProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [geolocations, setGeolocations] = useState({});
  const [isSubscribed, setIsSubscribed] = useState(false);
  const pollingIntervalRef = useRef(null);
  const { user } = useAuth();
  const dispatch = useDispatch();

  // RTK Query for initial load and polling fallback
  const {
    data: initialGeolocations,
    isLoading,
    refetch,
  } = useGetGeolocationsQuery(
    { freshnessMinutes: FRESHNESS_MINUTES },
    {
      skip: !user,
      pollingInterval: isConnected ? 0 : POLLING_INTERVAL, // Only poll when WebSocket is disconnected
    }
  );

  // Get parent company ID from user
  const companyId = user?.parentCompany || user?.company;

  /**
   * Initialize geolocations from RTK Query data
   */
  useEffect(() => {
    if (initialGeolocations && Array.isArray(initialGeolocations)) {
      const geoMap = {};
      initialGeolocations.forEach((geo) => {
        if (geo.userId && geo.location) {
          geoMap[geo.userId] = {
            userId: geo.userId,
            fullName: geo.fullName,
            avatar: geo.avatar,
            location: geo.location,
            accuracy: geo.accuracy,
            heading: geo.heading,
            speed: geo.speed,
            timestamp: geo.timestamp,
            updatedAt: geo.updatedAt,
          };
        }
      });
      setGeolocations((prev) => ({ ...prev, ...geoMap }));
    }
  }, [initialGeolocations]);

  /**
   * Socket.IO connection and subscription
   */
  useEffect(() => {
    if (!user || !companyId) return;

    const socketUrl = getSocketUrl();

    console.log("[GeoClient] Initializing socket", {
      url: socketUrl,
      companyId,
    });

    const newSocket = createSocketClient();

    // Connection handlers
    newSocket.on("connect", () => {
      console.log("[GeoClient] Connected, id:", newSocket.id);
      setIsConnected(true);

      // Subscribe to geolocation updates for this company
      newSocket.emit("geolocation:subscribe", { companyId });
    });

    newSocket.on("connect_error", (err) => {
      console.warn("[GeoClient] connect_error:", err?.message);
      setIsConnected(false);
    });

    newSocket.on("disconnect", (reason) => {
      console.log("[GeoClient] disconnected:", reason);
      setIsConnected(false);
      setIsSubscribed(false);
    });

    // Subscription confirmation
    newSocket.on("geolocation:subscribed", (data) => {
      console.log("[GeoClient] Subscribed to room:", data.room);
      setIsSubscribed(true);
    });

    // Real-time geolocation updates
    newSocket.on("geolocation:update", (data) => {
      console.log("[GeoClient] Received update for user:", data.userId);

      // Update local state
      setGeolocations((prev) => ({
        ...prev,
        [data.userId]: {
          userId: data.userId,
          fullName: data.fullName,
          avatar: data.avatar,
          location: data.location,
          accuracy: data.accuracy,
          heading: data.heading,
          speed: data.speed,
          timestamp: data.timestamp,
          updatedAt: data.updatedAt,
        },
      }));

      // Invalidate RTK Query cache for this user
      dispatch(
        geolocationApi.util.invalidateTags([
          { type: "Geolocation", id: data.userId },
        ])
      );
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      if (newSocket) {
        newSocket.emit("geolocation:unsubscribe", { companyId });
        newSocket.close();
      }
    };
  }, [user, companyId, dispatch]);

  /**
   * Get geolocation for a specific user
   */
  const getGeolocation = useCallback(
    (userId) => {
      return geolocations[userId] || null;
    },
    [geolocations]
  );

  /**
   * Get all geolocations as an array
   */
  const getAllGeolocations = useCallback(() => {
    return Object.values(geolocations);
  }, [geolocations]);

  /**
   * Check if a location is fresh (within freshness threshold)
   */
  const isLocationFresh = useCallback((updatedAt) => {
    if (!updatedAt) return false;
    const threshold = new Date(Date.now() - FRESHNESS_MINUTES * 60 * 1000);
    return new Date(updatedAt) > threshold;
  }, []);

  /**
   * Force refresh all geolocations
   */
  const refreshGeolocations = useCallback(() => {
    refetch();
  }, [refetch]);

  const value = {
    // State
    geolocations,
    isLoading,
    isConnected,
    isSubscribed,

    // Methods
    getGeolocation,
    getAllGeolocations,
    isLocationFresh,
    refreshGeolocations,

    // Raw socket for advanced usage
    socket,
  };

  return (
    <GeolocationContext.Provider value={value}>
      {children}
    </GeolocationContext.Provider>
  );
};

/**
 * useGeolocation Hook
 * Provides access to the geolocation context
 */
export const useGeolocation = () => {
  const context = useContext(GeolocationContext);
  if (!context) {
    throw new Error(
      "useGeolocation must be used within a GeolocationProvider"
    );
  }
  return context;
};

export default GeolocationContext;

